/**
 * LandingTestimonials Component
 *
 * Infinite horizontal scroll carousel of testimonials.
 * Features gradient fade on edges and pause on hover.
 */
"use client";

import Image from "next/image";

interface Testimonial {
  quote: string;
  name: string;
  title: string;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    quote: "Lev inspired me to kick off my business! I started with just a thought and now I'm launching",
    name: "Alex H.",
    title: "New Founder",
    avatar: "/landing/profiles/steve-jobs.png",
  },
  {
    quote: "The AI guidance helped me validate my idea before investing months of work",
    name: "Sarah M.",
    title: "First-time Founder",
    avatar: "/landing/profiles/oprah-winfrey.png",
  },
  {
    quote: "Finally, an AI that understands the founder journey from idea to execution",
    name: "Jordan T.",
    title: "Serial Entrepreneur",
    avatar: "/landing/profiles/jeff-bezos.png",
  },
  {
    quote: "Lev helped me think through my business model in ways I never would have on my own",
    name: "Chris L.",
    title: "Tech Founder",
    avatar: "/landing/profiles/mark-zuckerberg.png",
  },
  {
    quote: "The structured approach gave me confidence to actually pursue my startup idea",
    name: "Taylor R.",
    title: "Startup Builder",
    avatar: "/landing/profiles/taylor-swift.png",
  },
];

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <div className="flex-shrink-0 w-[320px] md:w-[380px] bg-white rounded-2xl p-6 shadow-sm">
      <p className="text-landing-dark text-base leading-relaxed mb-6">&ldquo;{testimonial.quote}&rdquo;</p>
      <div className="flex items-center gap-3">
        <div className="relative w-10 h-10 rounded-full overflow-hidden bg-landing-bg-medium">
          <Image
            src={testimonial.avatar}
            alt={testimonial.name}
            fill
            className="object-cover"
            sizes="40px"
          />
        </div>
        <div>
          <p className="text-landing-dark font-medium text-sm">{testimonial.name}</p>
          <p className="text-landing-secondary text-xs">{testimonial.title}</p>
        </div>
      </div>
    </div>
  );
}

export function LandingTestimonials() {
  // Duplicate testimonials for seamless infinite scroll
  const duplicatedTestimonials = [...testimonials, ...testimonials];

  return (
    <section className="bg-landing-bg-light py-20 overflow-hidden">
      <div className="w-full max-w-[1032px] mx-auto px-4 mb-12">
        <h2 className="text-[28px] md:text-[36px] text-landing-dark tracking-tight">
          Testimonials
        </h2>
      </div>
      <div className="relative">
        {/* Gradient fade left */}
        <div className="absolute left-0 top-0 bottom-0 w-20 md:w-40 bg-gradient-to-r from-landing-bg-light to-transparent z-10 pointer-events-none" />

        {/* Gradient fade right */}
        <div className="absolute right-0 top-0 bottom-0 w-20 md:w-40 bg-gradient-to-l from-landing-bg-light to-transparent z-10 pointer-events-none" />

        {/* Scrolling container */}
        <div
          className="flex gap-6 animate-scroll-left hover:[animation-play-state:paused]"
          style={{ "--scroll-duration": "40s" } as React.CSSProperties}
        >
          {duplicatedTestimonials.map((testimonial, index) => (
            <TestimonialCard key={`${testimonial.name}-${index}`} testimonial={testimonial} />
          ))}
        </div>
      </div>
    </section>
  );
}
